import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Minus, HandHeart, MapPin, Home, HelpCircle, BookOpenText } from "lucide-react";
import Signature from "../components/Signature";
import { mailtoLink } from "../config/contacts";

/* ── Assets ──────────────────────────────────────────────────────────── */
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/3au4x4mb_Screenshot%202026-05-27%20at%2011.33.42%E2%80%AFPM.png";

const IMG = {
  chicago:    "https://images.unsplash.com/photo-1494522855154-9297ac14b55f?auto=format&fit=crop&w=1200&q=80",
  carolina:   "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/o29wls1q_image%20of%20king%20nc.jpeg",
  notebook:   "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/qfg47bx8_1975-armageddon-prediction-v0-jzunfawgv5kd1.webp",
  shelves:    "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/2ggm3zjk_bell%20tower.jpeg",
  manuscript: "https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?auto=format&fit=crop&w=1200&q=80",
  mosque:     "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?auto=format&fit=crop&w=1200&q=80",
  classroom:  "https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=1200&q=80",
  arch:       "https://images.unsplash.com/photo-1564769662533-4f00a87b4056?auto=format&fit=crop&w=1200&q=80",
  desert:     "https://images.unsplash.com/photo-1518002171953-a080ee817e1f?auto=format&fit=crop&w=1200&q=80",
};

/* ── Shared atoms ────────────────────────────────────────────────────── */
const SectionEyebrow = ({ n, label }) => (
  <div className="flex items-center gap-4">
    <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
      {n} — {label}
    </span>
    <span className="h-px w-12 bg-azure-soft/50" />
  </div>
);

const AmbientLayer = ({ variant = "a" }) => {
  const variants = {
    a: "radial-gradient(ellipse 55% 50% at 80% 12%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 45% 45% at 8% 92%, rgba(201,169,97,0.05), transparent 70%)",
    b: "radial-gradient(ellipse 45% 50% at 12% 18%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 50% 50% at 92% 80%, rgba(74,143,229,0.06), transparent 70%)",
    c: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 40% 40% at 50% 100%, rgba(201,169,97,0.05), transparent 70%)",
  };
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: variants[variant] }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 inset-x-0 h-32"
        style={{ background: "linear-gradient(to bottom, rgba(6,19,37,0.55), transparent)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 inset-x-0 h-32"
        style={{ background: "linear-gradient(to top, rgba(6,19,37,0.4), transparent)" }}
      />
    </>
  );
};

/* Centered chapter header — used by all chapter sections to keep them in sync */
const ChapterHeader = ({ n, label, title, italicTail, intro }) => (
  <div className="text-center max-w-3xl mx-auto">
    <SectionEyebrow n={n} label={label} />
    <h2 className="mt-7 font-serif text-gold text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.06] tracking-[-0.012em]">
      {n}. {title}
      {italicTail && (
        <>
          {" "}
          <span className="italic text-azure-soft">{italicTail}</span>
        </>
      )}
    </h2>
    {intro && (
      <p className="mt-6 text-white/70 text-[15px] lg:text-[16px] leading-[1.9]">
        {intro}
      </p>
    )}
    <div className="mt-8 h-px w-12 bg-gold mx-auto" />
  </div>
);

/* Closing band — unifies the bottom of each chapter section */
const ChapterCloser = ({ headline, italicTail, body, ctaLabel, ctaHref, ctaTestId }) => (
  <div className="mt-12 lg:mt-16 border border-gold/35 bg-ink-deep/60">
    <div className="relative overflow-hidden">
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 100% at 100% 0%, rgba(201,169,97,0.10), transparent 65%)",
        }}
      />
      <div className="relative p-8 sm:p-10 lg:p-12 grid lg:grid-cols-[auto_1fr_auto] gap-6 lg:gap-10 items-center">
        <span className="w-12 h-12 rounded-full border border-gold/50 inline-flex items-center justify-center text-gold shrink-0">
          <HandHeart className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <div>
          <h3 className="font-serif text-white text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.18] tracking-[-0.01em]">
            {headline}
            {italicTail && (
              <>
                {" "}
                <span className="italic text-azure-soft">{italicTail}</span>
              </>
            )}
            .
          </h3>
          {body && (
            <p className="mt-3 text-white/65 text-[14px] leading-[1.85] max-w-2xl">{body}</p>
          )}
        </div>
        <a
          href={ctaHref}
          data-testid={ctaTestId}
          className="group inline-flex items-center justify-center gap-3 bg-gold hover:bg-[#D4B872] text-ink-deep px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-semibold transition-all whitespace-nowrap"
        >
          {ctaLabel}
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.8} />
        </a>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   1 — OPENING PORTRAIT
   ───────────────────────────────────────────────────────────────────── */
const SectionOpening = () => (
  <section data-testid="about-opening" className="relative overflow-hidden bg-ink">
    <AmbientLayer variant="a" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-16 lg:pb-20">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Portrait */}
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={PORTRAIT}
              alt="Iyare Omoruyi"
              className="w-full h-full object-cover"
              style={{ objectPosition: "55% 30%" }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 50%, rgba(6,19,37,0.8) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle 320px at 15% 20%, rgba(201,169,97,0.12), transparent 60%)",
              }}
            />
            <div className="absolute inset-0 border border-azure-soft/15" />
          </div>
          <div className="mt-7">
            <Signature className="h-14" />
          </div>
        </div>

        {/* Editorial intro */}
        <div className="lg:col-span-7 lg:pt-6">
          <SectionEyebrow n="01" label="A Brief Introduction" />
          <h1 className="mt-9 font-serif text-white text-[28px] sm:text-[38px] lg:text-[46px] leading-[1.04] tracking-[-0.022em]">
            A life shaped by questions,
            <br />
            study, mentorship, and{" "}
            <span className="italic text-azure-soft">formation.</span>
          </h1>
          <div className="mt-10 h-px w-20 bg-gold/80" />
          <div className="mt-10 space-y-6 text-white/75 text-[16.5px] leading-[1.95] max-w-2xl">
            <p>
              I am a proud first-generation Nigerian-American, born in Chicago
              and raised in King, North Carolina. I came to Islam as a student
              at North Carolina State University after years of restless
              searching — and I have been studying, teaching, and trying to
              live it ever since.
            </p>
            <p>
              The work that occupies me now is not the work I once imagined.
              It is the quiet, demanding work of <em className="text-azure-soft not-italic">tarbiyah</em> — the
              cultivation of human beings. Of helping young Muslims become
              anchored, awakened, and advancing in a world that prefers them
              distracted.
            </p>
            <p>
              What follows is the long way through.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   2 — ROOTS  (mosaic redesign — sample-inspired)
   ───────────────────────────────────────────────────────────────────── */
const ROOTS_PILLARS = [
  {
    Icon: MapPin,
    label: "Chicago — Born",
    body: "Born in Chicago. The discipline of an immigrant household and the quiet ambition of being first in a new country.",
    image: IMG.chicago,
    alt: "Chicago skyline",
  },
  {
    Icon: Home,
    label: "Raised — Carolina",
    body: "King, North Carolina by upbringing. South Stokes for high school. The rhythms of the rural South never leave you.",
    image: IMG.carolina,
    alt: "King, North Carolina water tower against open sky",
    lightOverlay: true,
    objectPosition: "center 45%",
  },
  {
    Icon: HelpCircle,
    label: "The Questions",
    body: "Raised in a Jehovah's Witness household. A devout student of the Watchtower — and yet, quietly, I held my reservations.",
    image: IMG.notebook,
    alt: "Watchtower magazine cover — the literature of a childhood faith",
    lightOverlay: true,
    objectPosition: "center 35%",
  },
  {
    Icon: BookOpenText,
    label: "Engineering · Reading",
    body: "Electrical engineering at N.C. State. Long evenings of reading, listening, and learning to think before answering.",
    image: IMG.shelves,
    alt: "N.C. State bell tower — campus years",
    lightOverlay: true,
    objectPosition: "center 30%",
  },
];

const SectionRoots = () => (
  <section data-testid="about-roots" className="relative overflow-hidden bg-ink-deep">
    <AmbientLayer variant="b" />
    <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
      {/* Centered chapter header */}
      <ChapterHeader
        n="02"
        label="Roots"
        title="Where the journey began."
        intro="Chicago by birth. King, North Carolina by upbringing. South Stokes for high school. Electrical engineering at N.C. State. The places that shaped a way of seeing — quiet, attentive, restless for meaning."
      />

      {/* About this chapter — split */}
      <div className="mt-14 grid lg:grid-cols-2 gap-0 border border-white/10 bg-ink-soft/30 overflow-hidden">
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
            About This Chapter
          </div>
          <h3 className="mt-5 font-serif text-white text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.1] tracking-[-0.012em]">
            Before the journey outward
            <br />
            came the{" "}
            <span className="italic text-azure-soft">questions within.</span>
          </h3>
          <div className="mt-7 h-px w-12 bg-gold" />
          <div className="mt-7 space-y-5 text-white/70 text-[14.5px] leading-[1.9] max-w-md">
            <p>
              I was raised in a Jehovah&apos;s Witness household by way of my
              mother. My father left doctrinal matters to her and made sure
              religion stayed practical — applied to lived life, not abstracted
              from it.
            </p>
            <p>
              I was always intrigued by religion. I was a devout student of the
              Watchtower&apos;s teachings, and yet, quietly, I held my
              reservations. The questions came early. The answers would take
              longer.
            </p>
          </div>
          <a
            href="#about-searching"
            data-testid="about-roots-continue"
            className="group mt-9 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-gold hover:text-white transition-colors"
          >
            Continue to The Searching
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.8} />
          </a>
        </div>
        <div className="relative min-h-[280px] lg:min-h-[460px]">
          <img
            src={IMG.shelves}
            alt="N.C. State bell tower — campus years of reading and listening"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 35%" }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(8,13,28,0.0) 70%, rgba(8,13,28,0.25) 100%)",
            }}
          />
        </div>
      </div>

      {/* 4-pillar mosaic */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:border lg:border-white/10">
        {ROOTS_PILLARS.map((p, i) => (
          <React.Fragment key={p.label}>
            <div className="relative aspect-[5/6] lg:aspect-auto lg:min-h-[320px] overflow-hidden lg:border-r lg:border-white/10 border border-white/10 lg:border-y-0 lg:border-l-0">
              <img
                src={p.image}
                alt={p.alt}
                className="absolute inset-0 w-full h-full object-cover"
                style={p.objectPosition ? { objectPosition: p.objectPosition } : undefined}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background: p.lightOverlay
                    ? "linear-gradient(180deg, rgba(8,13,28,0.0) 60%, rgba(8,13,28,0.30) 100%)"
                    : "linear-gradient(180deg, rgba(8,13,28,0.18) 0%, rgba(8,13,28,0.55) 100%)",
                }}
              />
            </div>
            <div
              data-testid={`about-roots-pillar-${i}`}
              className="group relative bg-ink-soft/40 hover:bg-ink-soft/60 transition-colors duration-500 p-7 lg:p-8 flex flex-col justify-center min-h-[280px] lg:min-h-[320px] border border-white/10 lg:border-0 lg:border-r lg:border-white/10 last:lg:border-r-0"
            >
              <div className="w-10 h-10 rounded-full border border-gold/45 flex items-center justify-center text-gold">
                <p.Icon className="w-4 h-4" strokeWidth={1.6} />
              </div>
              <div className="mt-5 text-[10.5px] tracking-[0.34em] uppercase text-gold">
                {p.label}
              </div>
              <p className="mt-4 text-white/70 text-[14px] leading-[1.85]">
                {p.body}
              </p>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Closing band */}
      <ChapterCloser
        headline="Every formation begins with"
        italicTail="a question"
        body="The questions of childhood became the work of a lifetime."
        ctaLabel="The Searching"
        ctaHref="#about-searching"
        ctaTestId="about-roots-next-chapter"
      />
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   3 — SEARCHING
   ───────────────────────────────────────────────────────────────────── */
const STATIONS = [
  { n: "i.",   label: "Watchtower",         body: "The faith of my childhood, taken seriously, then quietly questioned." },
  { n: "ii.",  label: "Church Debates",     body: "Late evenings on campus — the Triangle Church, Gary the Preacher, my first arguments." },
  { n: "iii.", label: "Comparative Religion", body: "Books, lectures, conversations. Everything became a question." },
  { n: "iv.",  label: "5% Nation",          body: "Through Wu-Tang and the words of a generation hungry for cosmology." },
  { n: "v.",   label: "Rastafarianism",     body: "A pull toward something older, rooted, defiant of modernity." },
  { n: "vi.",  label: "Islam",              body: "The one that, after everything, made sense." },
];

const SectionSearching = () => (
  <section data-testid="about-searching" className="relative overflow-hidden bg-ink">
    <AmbientLayer variant="c" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
      <div className="max-w-3xl">
        <SectionEyebrow n="03" label="Searching" />
        <h2 className="mt-9 font-serif text-white text-[28px] sm:text-[38px] lg:text-[46px] leading-[1.04] tracking-[-0.022em]">
          Searching for{" "}
          <span className="italic text-azure-soft">coherence.</span>
        </h2>
        <p className="mt-9 text-white/65 text-[16px] leading-[1.95] max-w-2xl">
          At university the inward questioning broke the surface. I battled
          the campus churches. I read on many religions — some I took
          seriously, others I did not. Each one taught me what I was looking
          for and what I was not.
        </p>
      </div>

      {/* Restrained horizontal timeline */}
      <div className="mt-12 lg:mt-14 relative">
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 top-7 hidden lg:block h-px"
          style={{ background: "linear-gradient(to right, transparent, rgba(74,143,229,0.30), transparent)" }}
        />
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-6">
          {STATIONS.map((s, i) => (
            <li key={s.label} className="relative">
              <div className="hidden lg:flex justify-center mb-6">
                <span
                  className={`w-3 h-3 rounded-full ring-4 ring-ink ${
                    i === STATIONS.length - 1 ? "bg-azure" : "bg-azure-soft/60"
                  }`}
                />
              </div>
              <div className="lg:text-center">
                <div className="font-serif italic text-azure-soft text-[16px] lg:text-[18px] leading-none">
                  {s.n}
                </div>
                <div className="mt-3 font-serif text-white text-[18px] lg:text-[20px] leading-[1.2] tracking-[-0.005em]">
                  {s.label}
                </div>
                <p className="mt-4 text-white/60 text-[13.5px] leading-[1.75] lg:max-w-[20ch] lg:mx-auto">
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   4 — STATIONS OF STUDY (Bahrain → Egypt → UAE)
   ───────────────────────────────────────────────────────────────────── */
const CHAPTERS = [
  {
    n: "Chapter I",
    place: "Bahrain",
    image: "/about-bahrain.png",
    body: [
      "Upon taking my shahadah I gave myself to Arabic and the basics of Islam. After graduation, I packed up my small family and we made for Bahrain — a sacrifice my wife and I have never forgotten.",
      "In Bahrain the language became the doorway. I perfected my spoken Arabic and sat with Sheikh Fawzi al-Athari and the students who orbited him, working through the fundamentals of the religion as if I were beginning everything for the first time.",
    ],
  },
  {
    n: "Chapter II",
    place: "Egypt",
    image: "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/7cwhs84d_me%20studying%20in%20Egypt.png",
    body: [
      "From Bahrain we travelled to Egypt. I deepened my Arabic at Markaz al-Ibaanah and Markaz al-Nile, but the mosque became both classroom and refuge — a place where one could be still long enough to learn.",
      "There I sat for three years with scholars and their students — Sheikh Hasan Abd al-Wahhab al-Banna, Sheikh Osama al-Qoosi, Sheikh Muhammad Ibrahim, Sheikh Khalid Abu Abd al-A'la, Sheikh Majdi Sultan and others — each one teaching not only knowledge but the manners with which knowledge must be carried.",
    ],
  },
  {
    n: "Chapter III",
    place: "United Arab Emirates",
    image: "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/8s9ay3zs_me%20studying%20in%20uae%20v2.png",
    body: [
      "After three years in Egypt we moved to the UAE. My focus turned toward the Hanbali fiqh tradition — Akhsar al-Mukhtasaraat, Daleel al-Taalib, Manaar al-Sabeel, Rawd al-Murbi' — with Sheikh Aziz bin Farhan, whose character taught me as much as his texts.",
      "Sheikh Bashir al-Saadani walked me through al-Aajaroomiyah and Alfiyah al-Suyooti. Sheikh Ahmed Abdulkareem opened Sahih Muslim and Tirmidhi. And then there was Sheikh Abdul-Basit al-Arkaani — rahimahullah — who, if I ever had a spiritual guide, was him. He showed me a softer face of the religion, taught me tolerance, taught me how to love the Muslims. He introduced me to the deeper currents of the Hanbali madhhab and pointed me toward al-Madkhal. May Allah make his grave spacious and restful.",
    ],
  },
];

const SectionStations = () => (
  <section data-testid="about-stations" className="relative overflow-hidden bg-ink-deep">
    <AmbientLayer variant="a" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
      <div className="max-w-3xl">
        <SectionEyebrow n="04" label="Stations of Study" />
        <h2 className="mt-9 font-serif text-white text-[28px] sm:text-[38px] lg:text-[46px] leading-[1.04] tracking-[-0.022em]">
          Three countries.
          <br />
          One <span className="italic text-azure-soft">long apprenticeship.</span>
        </h2>
        <p className="mt-9 text-white/65 text-[16px] leading-[1.95] max-w-2xl">
          What followed conversion was not a degree. It was a decade of
          travel, sacrifice, and time at the feet of teachers who shaped me
          quietly, in ways I am still discovering.
        </p>
      </div>

      <div className="mt-14 lg:mt-18 space-y-20 lg:space-y-28">
        {CHAPTERS.map((c, i) => {
          const reverse = i % 2 === 1;
          return (
            <article
              key={c.place}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                reverse ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-5">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={c.image} alt={c.place} className="w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-[1.04]" />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(6,19,37,0.6) 0%, transparent 50%), radial-gradient(circle 360px at 15% 18%, rgba(201,169,97,0.10), transparent 60%)",
                    }}
                  />
                  <div className="absolute inset-0 border border-white/10" />
                  <div className="absolute bottom-6 left-7 right-7">
                    <div className="text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
                      {c.n}
                    </div>
                    <div className="mt-3 font-serif italic text-white text-[28px] lg:text-[34px] leading-none tracking-[-0.01em]">
                      {c.place}
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7 space-y-6 text-white/75 text-[16px] leading-[1.95]">
                {c.body.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   5 — THE MURABBI TURNING POINT
   ───────────────────────────────────────────────────────────────────── */
const SectionTurningPoint = () => (
  <section data-testid="about-turning-point" className="relative overflow-hidden bg-ink">
    <AmbientLayer variant="c" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <SectionEyebrow n="05" label="The Turning Point" />
          <h2 className="mt-9 font-serif text-white text-[24px] sm:text-[30px] lg:text-[36px] leading-[1.04] tracking-[-0.022em]">
            Education became{" "}
            <span className="italic text-azure-soft">formation.</span>
          </h2>
          <div className="mt-9 h-px w-16 bg-gold/80" />
          <div className="mt-9 space-y-6 text-white/75 text-[16.5px] leading-[1.95] max-w-xl">
            <p>
              In the UAE I joined a faith-based school that quietly rearranged
              everything. I met some of the most remarkable people I have ever
              known — teachers, parents, and students who insisted that
              education had a soul.
            </p>
            <p>
              There I had the gift of working alongside a mentor of my own
              youth: <em className="text-azure-soft not-italic">Mumtaz Johny Nobles</em>. He
              showed us — the other teachers and I — what it actually means
              to mentor. Not to deliver content, but to be a{" "}
              <em className="text-azure-soft not-italic">murabbi</em> — one who fosters
              the building of Islamic character in another human being.
            </p>
            <p>
              From that point forward, the question was no longer what to
              teach. The question was who to become.
            </p>
          </div>

          {/* Pull-quote */}
          <figure className="mt-12 relative max-w-lg">
            <span
              aria-hidden="true"
              className="absolute -left-3 -top-6 font-serif text-azure-soft/45 text-[88px] leading-none select-none pointer-events-none"
            >
              &ldquo;
            </span>
            <blockquote className="relative pl-7 border-l border-azure-soft/30">
              <p className="font-serif italic text-white/90 text-[16px] lg:text-[18px] leading-[1.5] tracking-[-0.005em]">
                You are shaping futures — not just minds,{" "}
                <span className="text-azure-soft">but hearts.</span>
              </p>
            </blockquote>
            <figcaption className="mt-5 pl-7 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
              — From &ldquo;The Way of the Murabbi&rdquo;
            </figcaption>
          </figure>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/709a95ya_me%20teaching%20math%20v2.png"
              alt="Iyare teaching mathematics in a UAE classroom"
              className="w-full h-full object-cover"
              style={{ objectPosition: "68% 35%" }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(6,19,37,0.15) 0%, transparent 30%), radial-gradient(circle 420px at 18% 18%, rgba(201,169,97,0.08), transparent 60%), radial-gradient(circle 360px at 82% 90%, rgba(74,143,229,0.08), transparent 70%)",
              }}
            />
            <div className="absolute inset-0 border border-white/10" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   6 — PRINCIPLES & CONVICTIONS (expandable, elegant, restrained)
   ───────────────────────────────────────────────────────────────────── */
const PRINCIPLES = [
  {
    title: "Grounded in Sunni Orthodoxy.",
    body:
      "I take seriously the statement of Imam al-Saffarini that Ahlu Sunnah are three streams — the Atharis, the Asharis and the Maturidiyyah. I hold my own position with conviction without forgetting that the wider Sunni house is wide on purpose.",
  },
  {
    title: "Hanbali by madhhab — with respect for all four.",
    body:
      "My fiqh framework is Hanbali. I am formed by its texts and its scholars. But the four schools are a mercy to this ummah, and I am unwilling to weaponise my madhhab against them.",
  },
  {
    title: "Salafi roots, not Salafi exclusivism.",
    body:
      "The majority of my formal study has been with scholars in the Salafi tradition and I am blessed to continue with them. That lineage does not, however, define the boundaries of what I am willing to learn from.",
  },
  {
    title: "Tarbiyah over polemics.",
    body:
      "I treat every Sunni Muslim as a brother. Clarifying belief, when it is necessary, is an act of care for the ummah rather than a tool for division. Truth and brotherhood are not opposites.",
  },
  {
    title: "Tazkiyah matters.",
    body:
      "Call it tazkiyat al-nafs, zuhd, or akhlaq — the inner life of the Muslim cannot be skipped. I believe in a spirituality grounded in the Book and the Sunnah; what is not from the tradition is not what I am inviting people toward.",
  },
  {
    title: "Honouring the scholars, without idolising them.",
    body:
      "I have benefited deeply from many scholars, including Sheikh Muhammad ibn Abd al-Wahhab, whose works have served me. I also believe — as the scholars themselves taught — that no human teacher is beyond gentle, principled correction.",
  },
];

const Disclosure = ({ p, open, onToggle, index }) => (
  <li className="group border-t border-white/10 last:border-b">
    <button
      data-testid={`principle-toggle-${index}`}
      onClick={onToggle}
      className="w-full text-left py-8 lg:py-10 flex items-start gap-6 transition-colors duration-500"
    >
      <span className="font-serif italic text-azure-soft text-[18px] lg:text-[20px] leading-none w-12 shrink-0">
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="flex-1">
        <h3
          className={`font-serif text-[19px] lg:text-[22px] leading-[1.25] tracking-[-0.012em] transition-colors duration-500 ${
            open ? "text-azure-soft" : "text-white group-hover:text-azure-soft"
          }`}
        >
          {p.title}
        </h3>
        <div
          className={`grid transition-all duration-500 ease-out ${
            open ? "grid-rows-[1fr] opacity-100 mt-5" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <p className="text-white/70 text-[15.5px] leading-[1.95] max-w-2xl">
              {p.body}
            </p>
          </div>
        </div>
      </div>
      <span className="shrink-0 mt-1 text-azure-soft transition-transform duration-500">
        {open ? <Minus className="w-4 h-4" strokeWidth={1.6} /> : <Plus className="w-4 h-4" strokeWidth={1.6} />}
      </span>
    </button>
  </li>
);

const SectionPrinciples = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="about-principles" className="relative overflow-hidden bg-ink-deep">
      <AmbientLayer variant="b" />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <SectionEyebrow n="06" label="Principles & Convictions" />
              <h2 className="mt-9 font-serif text-white text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.04] tracking-[-0.02em]">
                Where I{" "}
                <span className="italic text-azure-soft">stand.</span>
              </h2>
              <p className="mt-9 text-white/65 text-[15.5px] leading-[1.95] max-w-md">
                Not a creed in miniature. A short statement of the
                commitments that shape how I teach, how I write, and how I
                hold company with the rest of the ummah.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ol>
              {PRINCIPLES.map((p, i) => (
                <Disclosure
                  key={p.title}
                  p={p}
                  index={i}
                  open={open === i}
                  onToggle={() => setOpen(open === i ? -1 : i)}
                />
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   7 — PATH OF STUDY
   ───────────────────────────────────────────────────────────────────── */
const PATH = [
  {
    eyebrow: "Engineering",
    items: [
      { t: "B.S. Electrical Engineering", i: "North Carolina State University" },
    ],
  },
  {
    eyebrow: "Islamic Studies",
    items: [
      { t: "B.A. Da'wah wa Usool al-Deen", i: "Al-Madina International University · incomplete" },
      { t: "B.A. Hanbali Fiqh", i: "The Hanbali College / Islamic University of Minnesota · in progress" },
      { t: "Ma'had al-Buhuti", i: "Year One" },
      { t: "Diploma — General Studies", i: "International Open University" },
      { t: "Certificate", i: "Al-Murtaqaa Academy · Year Two" },
    ],
  },
];

const SectionPath = () => (
  <section data-testid="about-path" className="relative overflow-hidden bg-ink">
    <AmbientLayer variant="a" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
      <div className="max-w-3xl">
        <SectionEyebrow n="07" label="Path of Study" />
        <h2 className="mt-9 font-serif text-white text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.04] tracking-[-0.02em]">
          Where the road{" "}
          <span className="italic text-azure-soft">has taken me.</span>
        </h2>
      </div>

      <div className="mt-12 lg:mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {PATH.map((group) => (
          <div key={group.eyebrow} className={group.eyebrow === "Engineering" ? "lg:col-span-4" : "lg:col-span-8"}>
            <div className="text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
              {group.eyebrow}
            </div>
            <div className="mt-7 space-y-5">
              {group.items.map((it) => (
                <div
                  key={it.t}
                  className="group relative p-6 lg:p-7 border border-white/10 bg-ink-soft/30 hover:border-azure/40 transition-colors duration-500"
                >
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(74,143,229,0.08), transparent 70%)",
                    }}
                  />
                  <div className="relative">
                    <h3 className="font-serif text-white text-[16px] lg:text-[18px] leading-[1.25] tracking-[-0.005em]">
                      {it.t}
                    </h3>
                    <p className="mt-3 text-white/55 text-[13.5px] tracking-wide">
                      {it.i}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   8 — MISSION
   ───────────────────────────────────────────────────────────────────── */
const SectionMission = () => (
  <section data-testid="about-mission" className="relative overflow-hidden bg-ink-deep">
    <AmbientLayer variant="c" />
    <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 py-14 lg:py-18 text-center">
      <div className="inline-flex items-center gap-4">
        <span className="h-px w-12 bg-azure-soft/50" />
        <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
          08 — The Work Now
        </span>
        <span className="h-px w-12 bg-azure-soft/50" />
      </div>
      <h2 className="mt-10 font-serif text-white text-[24px] sm:text-[30px] lg:text-[38px] leading-[1.03] tracking-[-0.022em]">
        Youth mentorship,
        <br />
        the murabbi vision,{" "}
        <span className="italic text-azure-soft">and beyond the classroom.</span>
      </h2>
      <p className="mt-10 text-white/75 text-[17px] leading-[1.95] max-w-2xl mx-auto">
        The mission now is plain: to take the murabbi tradition beyond the
        walls of any one school and into the communities that need it.
        Mentorship, formation, and a generation of young Muslims raised
        anchored, awakened, and advancing.
      </p>
      <div className="mt-14 h-px w-20 bg-gold/80 mx-auto" />
      <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/consulting"
          data-testid="mission-cta-mentorship"
          className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
        >
          Mentorship Programs
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
        <Link
          to="/speaking"
          data-testid="mission-cta-speaking"
          className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
        >
          Speaking
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
        <Link
          to="/podcast"
          data-testid="mission-cta-podcast"
          className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
        >
          Podcast
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
        <a
          href={mailtoLink("me", "Personal Correspondence")}
          data-testid="mission-cta-contact"
          className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
        >
          Contact Iyare
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </a>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────────── */
const About = () => (
  <main data-testid="about-page" className="bg-ink">
    <SectionOpening />
    <SectionRoots />
    <SectionSearching />
    <SectionStations />
    <SectionTurningPoint />
    <SectionPrinciples />
    <SectionPath />
    <SectionMission />
  </main>
);

export default About;
